import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "../api/client.js";
import { useAuth } from "../context/AuthContext.jsx";
import { useI18n } from "../i18n/I18nContext.jsx";
import OfficerSelect from "./OfficerSelect.jsx";
import OfficerMultiSelect from "./OfficerMultiSelect.jsx";

// Assignment dialog — implements the handwritten flow:
//   Assign -> Individual (officer dropdown) | Combined (select zones + CUG no.)
export default function AssignModal({ para, onClose, onDone }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [assignType, setAssignType] = useState("INDIVIDUAL");
  const [reason, setReason] = useState(para.assign_reason || "");
  const [officer, setOfficer] = useState("");
  const [officers, setOfficers] = useState([]);
  const [zones, setZones] = useState([]);
  const [picked, setPicked] = useState({}); // zoneId -> cug_number
  const [pickedOfficers, setPickedOfficers] = useState([]); // officer ids
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  // A Zonal AC may only assign to officers posted in their own zone.
  const zoneLocked = user.role === "ZONAL_AC" && !!user.zone;

  useEffect(() => {
    // The main admin may assign to an officer (AE) or a Zonal AC. A zone
    // login may only pick officers posted in its own zone — never an AC.
    const params = {
      role: zoneLocked ? "OFFICER" : "OFFICER,ZONAL_AC",
      page_size: 100,
    };
    if (zoneLocked) params.zone = user.zone;
    api
      .get("/auth/users/", { params })
      .then((r) => setOfficers(r.data.results || r.data))
      .catch(() => {});
    api
      .get("/zones/")
      .then((r) => setZones(r.data.results || r.data))
      .catch(() => {});
  }, [zoneLocked, user.zone]);

  const toggleZone = (z) => {
    setPicked((prev) => {
      const next = { ...prev };
      if (z.id in next) delete next[z.id];
      else next[z.id] = z.cug_number || "";
      return next;
    });
  };

  const submit = async () => {
    setError("");
    setBusy(true);
    const payload = { assign_type: assignType, reason };
    if (assignType === "INDIVIDUAL") {
      payload.officer = Number(officer);
    } else if (assignType === "MULTIPLE") {
      payload.officers = pickedOfficers;
    } else {
      payload.zones = Object.entries(picked).map(([zone, cug]) => ({
        zone: Number(zone),
        cug_number: cug,
      }));
    }
    try {
      await api.post(`/paras/${para.id}/assign/`, payload);
      onDone();
    } catch (e) {
      setError(
        e.response?.data?.detail ||
          JSON.stringify(e.response?.data || "Failed to assign.")
      );
      setBusy(false);
    }
  };

  const canSubmit =
    assignType === "INDIVIDUAL"
      ? !!officer
      : assignType === "MULTIPLE"
      ? pickedOfficers.length > 0
      : Object.keys(picked).length > 0;


  return createPortal(
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h3>
          {t("assignPara")} — {para.para_no}
        </h3>
        {error && <div className="error-text">{error}</div>}

        <div className="field">
          <label>{t("assignType")}</label>
          <select
            value={assignType}
            onChange={(e) => setAssignType(e.target.value)}
          >
            <option value="INDIVIDUAL">{t("individual")}</option>
            <option value="MULTIPLE">Multiple Officers</option>
            {!zoneLocked && (
              <option value="COMBINED">{t("combined")}</option>
            )}
          </select>
        </div>

        {assignType === "INDIVIDUAL" && (
          <div className="field">
            <label>{t("selectOfficer")}</label>
            {zoneLocked && (
              <div className="muted" style={{ fontSize: 11, marginBottom: 4 }}>
                Showing officers in your zone only.
              </div>
            )}
            <OfficerSelect
              officers={officers}
              value={officer}
              onChange={setOfficer}
            />
          </div>
        )}

        {assignType === "MULTIPLE" && (
          <div className="field">
            <label>Select Officers — each tracks their own status</label>
            <OfficerMultiSelect
              officers={officers}
              value={pickedOfficers}
              onChange={setPickedOfficers}
            />
          </div>
        )}

        {assignType === "COMBINED" && (
          <div className="field">
            <label>
              {t("selectZones")} — {t("cugNumber")}
            </label>
            {zones.map((z) => (
              <div
                key={z.id}
                style={{
                  display: "flex",
                  gap: 8,
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: 16 }}
                  checked={z.id in picked}
                  onChange={() => toggleZone(z)}
                />
                <span style={{ flex: 1 }}>
                  {z.name} ({z.code})
                </span>
                <input
                  style={{ width: 130 }}
                  placeholder={t("cugNumber")}
                  disabled={!(z.id in picked)}
                  value={picked[z.id] || ""}
                  onChange={(e) =>
                    setPicked((p) => ({ ...p, [z.id]: e.target.value }))
                  }
                />
              </div>
            ))}
          </div>
        )}

        <div className="field">
          <label>{t("assignReason")}</label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder={t("reason")}
          />
        </div>

        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            {t("cancel")}
          </button>
          <button
            className="btn btn-primary"
            disabled={busy || !canSubmit}
            onClick={submit}
          >
            {t("assign")}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
